import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../App';
import Navigation from './Navigation';
import Footer from './Footer';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Snowflake, 
  Flame, 
  Package, 
  Clock, 
  ArrowDown,
  ArrowRight,
  Thermometer,
  Timer,
  Coffee,
  Plus,
  Edit,
  Trash2,
  Save,
  X
} from 'lucide-react';

const ViennoiserieProduction = () => {
  const { user, API } = useContext(UserContext);
  const [viennoiserieProducts, setViennoiserieProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantityToFreeze, setQuantityToFreeze] = useState('');
  const [fridgeStock, setFridgeStock] = useState([]);
  const [cookingQuantity, setCookingQuantity] = useState('');
  const [selectedFridgeItem, setSelectedFridgeItem] = useState('');
  const [cookingRecords, setCookingRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [editingFridge, setEditingFridge] = useState(null);
  const [editingCooking, setEditingCooking] = useState(null);
  const [editFridgeData, setEditFridgeData] = useState({});
  const [editCookingData, setEditCookingData] = useState({});

  useEffect(() => {
    loadViennoiserieData();
  }, []);

  const loadViennoiserieData = async () => {
    try {
      setPageLoading(true);
      
      const [productsRes, fridgeRes, cookingRes] = await Promise.all([
        axios.get(`${API}/products/viennoiserie`).catch(() => ({ data: [] })),
        axios.get(`${API}/fridge-stock/summary`).catch(() => ({ data: [] })),
        axios.get(`${API}/cooking?date=${new Date().toISOString().split('T')[0]}`).catch(() => ({ data: [] }))
      ]);
      
      setViennoiserieProducts(productsRes.data || []);
      setFridgeStock(fridgeRes.data || []);
      setCookingRecords(cookingRes.data || []);
      
    } catch (error) {
      console.error('Error loading viennoiserie data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setPageLoading(false);
    }
  };

  const addToFridge = async (e) => {
    e.preventDefault();
    
    try {
      if (!selectedProduct || !quantityToFreeze || parseFloat(quantityToFreeze) <= 0) {
        toast.error('Veuillez sélectionner un produit et saisir une quantité valide');
        return;
      }

      setLoading(true);

      await axios.post(`${API}/fridge-stock`, {
        product_id: selectedProduct,
        quantity_frozen: parseFloat(quantityToFreeze),
        user_id: user.id,
        notes: 'Produits fabriqués et surgélés'
      });

      toast.success('Produits ajoutés au mini-stock du frigo !');
      
      // Reset form
      setSelectedProduct('');
      setQuantityToFreeze('');
      
      // Refresh data
      loadViennoiserieData();
      
    } catch (error) {
      console.error('Error adding to fridge:', error);
      toast.error('Erreur lors de l\'ajout au frigo');
    } finally {
      setLoading(false);
    }
  };

  const cookFromFridge = async (e) => {
    e.preventDefault();
    
    try {
      if (!selectedFridgeItem || !cookingQuantity || parseFloat(cookingQuantity) <= 0) {
        toast.error('Veuillez sélectionner un produit du frigo et saisir une quantité');
        return;
      }

      setLoading(true);

      await axios.post(`${API}/cooking`, {
        fridge_stock_id: selectedFridgeItem,
        quantity_cooked: parseFloat(cookingQuantity),
        user_id: user.id,
        notes: 'Cuisson pour le guichet viennoiserie'
      });

      toast.success('Produits cuits et envoyés au guichet viennoiserie !');
      
      // Reset form
      setSelectedFridgeItem('');
      setCookingQuantity('');
      
      // Refresh data
      loadViennoiserieData();
      
    } catch (error) {
      console.error('Error cooking from fridge:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la cuisson');
    } finally {
      setLoading(false);
    }
  };

  const canManage = () => {
    return user && ['manager', 'owner', 'system_admin'].includes(user.role);
  };

  // Gestion Mini-Stock Frigo
  const startEditFridge = (batch, parentItem) => {
    setEditingFridge(batch.id);
    setEditFridgeData({
      quantity: batch.quantity.toString(),
      notes: parentItem.notes || ''
    });
  };

  const cancelEditFridge = () => {
    setEditingFridge(null);
    setEditFridgeData({});
  };

  const saveEditFridge = async (batchId) => {
    try {
      if (!editFridgeData.quantity || parseFloat(editFridgeData.quantity) <= 0) {
        toast.error('Quantité invalide');
        return;
      }

      await axios.put(`${API}/fridge-stock/${batchId}?user_role=${user.role}`, {
        quantity_frozen: parseFloat(editFridgeData.quantity),
        notes: editFridgeData.notes
      });

      toast.success('Mini-stock frigo modifié !');
      
      setEditingFridge(null);
      setEditFridgeData({});
      loadViennoiserieData();
      
    } catch (error) {
      console.error('Error editing fridge stock:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const deleteFridgeItem = async (batchId, productName) => {
    try {
      if (!window.confirm(`Supprimer ce lot de "${productName}" du frigo ?`)) return;
      
      await axios.delete(`${API}/fridge-stock/${batchId}?user_role=${user.role}`);
      toast.success('Lot supprimé du frigo !');
      
      loadViennoiserieData();
      
    } catch (error) {
      console.error('Error deleting fridge item:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Gestion Enregistrements de Cuisson
  const startEditCooking = (record) => {
    setEditingCooking(record.id);
    setEditCookingData({
      quantity: record.quantity_cooked.toString(),
      notes: record.notes || ''
    });
  };

  const cancelEditCooking = () => {
    setEditingCooking(null);
    setEditCookingData({});
  };

  const saveEditCooking = async (recordId) => {
    try {
      if (!editCookingData.quantity || parseFloat(editCookingData.quantity) <= 0) {
        toast.error('Quantité invalide');
        return;
      }

      await axios.put(`${API}/cooking/${recordId}?user_role=${user.role}`, {
        quantity_cooked: parseFloat(editCookingData.quantity),
        notes: editCookingData.notes
      });

      toast.success('Enregistrement de cuisson modifié !');
      
      setEditingCooking(null);
      setEditCookingData({});
      loadViennoiserieData();
      
    } catch (error) {
      console.error('Error editing cooking record:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const deleteCookingRecord = async (recordId, productName) => {
    try {
      if (!window.confirm(`Supprimer cet enregistrement de cuisson de "${productName}" ?`)) return;
      
      await axios.delete(`${API}/cooking/${recordId}?user_role=${user.role}`);
      toast.success('Enregistrement de cuisson supprimé !');
      
      loadViennoiserieData();
      
    } catch (error) {
      console.error('Error deleting cooking record:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Heure inconnue';
    try {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Heure invalide';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR');
    } catch {
      return 'Date invalide';
    }
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement de la production viennoiserie...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
              <Coffee className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                🥐 Production Viennoiserie
              </h1>
              <p className="text-gray-600">
                Fabrication → Mini-stock Frigo → Cuisson → Guichet Viennoiserie
              </p>
            </div>
          </div>

          {/* Workflow Visuel */}
          <div className="flex items-center justify-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                <Package className="h-8 w-8 text-blue-600" />
              </div>
              <p className="text-sm font-medium">1. Fabrication</p>
            </div>
            <ArrowRight className="h-6 w-6 text-purple-500" />
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-2">
                <Snowflake className="h-8 w-8 text-indigo-600" />
              </div>
              <p className="text-sm font-medium">2. Mini-stock Frigo</p>
            </div>
            <ArrowRight className="h-6 w-6 text-purple-500" />
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-2">
                <Flame className="h-8 w-8 text-orange-600" />
              </div>
              <p className="text-sm font-medium">3. Cuisson</p>
            </div>
            <ArrowRight className="h-6 w-6 text-purple-500" />
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-2">
                <Coffee className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-sm font-medium">4. Guichet</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Étape 1: Fabrication → Frigo */}
          <Card className="bg-white/70 glass border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Snowflake className="h-5 w-5 text-indigo-600" />
                Étape 1: Fabrication → Mini-stock Frigo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={addToFridge} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Produit de viennoiserie
                  </Label>
                  <select
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                    className="w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                    required
                  >
                    <option value="">Sélectionnez un produit</option>
                    {viennoiserieProducts.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} - {product.price} F CFA
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Quantité fabriquée (à surgeler)
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0.1"
                    value={quantityToFreeze}
                    onChange={(e) => setQuantityToFreeze(e.target.value)}
                    className="h-12 text-base"
                    placeholder="Ex: 100 croissants"
                    required
                  />
                </div>

                <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Snowflake className="h-4 w-4 text-indigo-600" />
                    <p className="text-sm font-medium text-indigo-800">
                      Destination: Mini-stock du Frigo
                    </p>
                  </div>
                  <p className="text-xs text-indigo-700">
                    Les produits fabriqués seront automatiquement surgélés et stockés dans le frigo 
                    en attente de cuisson pour le guichet viennoiserie.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !selectedProduct || !quantityToFreeze}
                  className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium rounded-lg shadow-lg"
                >
                  {loading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Fabrication...
                    </>
                  ) : (
                    <>
                      <Snowflake className="h-4 w-4 mr-2" />
                      Fabriquer et Surgeler
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Étape 2: Cuisson Frigo → Guichet */}
          <Card className="bg-white/70 glass border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flame className="h-5 w-5 text-orange-600" />
                Étape 2: Cuisson → Guichet Viennoiserie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={cookFromFridge} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Produit du frigo à cuire
                  </Label>
                  <select
                    value={selectedFridgeItem}
                    onChange={(e) => setSelectedFridgeItem(e.target.value)}
                    className="w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 bg-white"
                    required
                  >
                    <option value="">Sélectionnez depuis le frigo</option>
                    {fridgeStock.map((item) => (
                      <option key={item._id} value={item.batches[0]?.id}>
                        {item.product_name} (Stock: {item.total_quantity} surgélés)
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Quantité à cuire
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0.1"
                    value={cookingQuantity}
                    onChange={(e) => setCookingQuantity(e.target.value)}
                    className="h-12 text-base"
                    placeholder="Ex: 30"
                    required
                  />
                  {selectedFridgeItem && (
                    <p className="text-xs text-gray-500">
                      Maximum disponible: {
                        fridgeStock.find(item => item.batches.some(b => b.id === selectedFridgeItem))?.total_quantity || 0
                      } unités
                    </p>
                  )}
                </div>

                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Flame className="h-4 w-4 text-orange-600" />
                    <p className="text-sm font-medium text-orange-800">
                      Destination: Guichet Viennoiserie
                    </p>
                  </div>
                  <p className="text-xs text-orange-700">
                    Les produits cuits seront automatiquement envoyés au guichet viennoiserie 
                    et enregistrés comme production prête à la vente.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !selectedFridgeItem || !cookingQuantity}
                  className="w-full h-12 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-medium rounded-lg shadow-lg"
                >
                  {loading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Cuisson...
                    </>
                  ) : (
                    <>
                      <Flame className="h-4 w-4 mr-2" />
                      Cuire et Envoyer au Guichet
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Mini-stock du Frigo */}
        <Card className="mt-8 bg-white/70 glass border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Snowflake className="h-5 w-5 text-indigo-600" />
                ❄️ Mini-stock du Frigo
              </div>
              <Badge variant="secondary" className="bg-indigo-100 text-indigo-800">
                {fridgeStock.length} type(s) de produit(s)
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {fridgeStock.length > 0 ? (
              <div className="space-y-4">
                {fridgeStock.map((item, index) => (
                  <div key={index} className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-lg flex items-center justify-center">
                          <Snowflake className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{item.product_name}</h4>
                          <p className="text-sm text-gray-600">
                            🗓️ Fabriqué du {formatDate(item.oldest_date)} au {formatDate(item.newest_date)}
                          </p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                            <span>❄️ {item.batches.length} lot(s) surgélé(s)</span>
                            <span>📊 {item.total_quantity} unités disponibles</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <Badge variant="secondary" className="bg-indigo-100 text-indigo-800 text-lg font-bold">
                          {item.total_quantity} surgélés
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">
                          Prêts pour cuisson
                        </p>
                      </div>
                    </div>

                    {/* Détail des lots avec boutons de gestion */}
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                      {item.batches.map((batch, batchIndex) => (
                        <div key={batchIndex} className="p-3 bg-white rounded-lg border border-indigo-200">
                          {editingFridge === batch.id ? (
                            /* Mode édition */
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 mb-2">
                                <Edit className="h-3 w-3 text-orange-600" />
                                <span className="text-xs font-medium text-orange-800">Modification Lot {batchIndex + 1}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <input
                                  type="number"
                                  step="0.1"
                                  value={editFridgeData.quantity || ''}
                                  onChange={(e) => setEditFridgeData(prev => ({...prev, quantity: e.target.value}))}
                                  className="px-2 py-1 text-sm border rounded"
                                  placeholder="Quantité"
                                />
                                <input
                                  type="text"
                                  value={editFridgeData.notes || ''}
                                  onChange={(e) => setEditFridgeData(prev => ({...prev, notes: e.target.value}))}
                                  className="px-2 py-1 text-sm border rounded"
                                  placeholder="Notes"
                                />
                              </div>
                              <div className="flex gap-1 justify-center">
                                <button
                                  onClick={() => saveEditFridge(batch.id)}
                                  className="p-1 bg-green-100 hover:bg-green-200 rounded text-green-600"
                                  title="Sauvegarder"
                                >
                                  <Save className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={cancelEditFridge}
                                  className="p-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-600"
                                  title="Annuler"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* Mode affichage */
                            <div>
                              <div className="flex justify-between items-center mb-2">
                                <span className="font-medium text-sm">Lot {batchIndex + 1}</span>
                                <Badge variant="outline" className="text-xs">
                                  {batch.quantity} surgélés
                                </Badge>
                              </div>
                              <p className="text-xs text-gray-600 mb-2">
                                🕒 {formatDate(batch.date)} à {formatTime(batch.time)}
                              </p>
                              
                              {/* Boutons de gestion */}
                              {canManage() && (
                                <div className="flex gap-1 justify-center">
                                  <button
                                    onClick={() => startEditFridge(batch, item)}
                                    className="p-1 bg-orange-100 hover:bg-orange-200 rounded transition-colors"
                                    title="Modifier ce lot"
                                    data-testid={`edit-fridge-${batch.id}`}
                                  >
                                    <Edit className="h-3 w-3 text-orange-600" />
                                  </button>
                                  <button
                                    onClick={() => deleteFridgeItem(batch.id, item.product_name)}
                                    className="p-1 bg-red-100 hover:bg-red-200 rounded transition-colors"
                                    title="Supprimer ce lot"
                                    data-testid={`delete-fridge-${batch.id}`}
                                  >
                                    <Trash2 className="h-3 w-3 text-red-600" />
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Snowflake className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Frigo Vide</h3>
                <p className="text-gray-600">Aucun produit surgélé en stock</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Historique de Cuisson */}
        <Card className="mt-8 bg-white/70 glass border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-600" />
              🔥 Produits Cuits Aujourd'hui
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cookingRecords.length > 0 ? (
              <div className="space-y-3">
                {cookingRecords.map((record, index) => (
                  <div key={record.id || index} className="p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200">
                    {editingCooking === record.id ? (
                      /* Mode édition cuisson */
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Edit className="h-4 w-4 text-orange-600" />
                          <p className="font-medium text-orange-900">
                            Modification: {record.product_name}
                          </p>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-gray-600">Quantité cuite</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={editCookingData.quantity || ''}
                              onChange={(e) => setEditCookingData(prev => ({...prev, quantity: e.target.value}))}
                              className="h-8 text-sm"
                              placeholder="Ex: 25"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-600">Notes</Label>
                            <Input
                              type="text"
                              value={editCookingData.notes || ''}
                              onChange={(e) => setEditCookingData(prev => ({...prev, notes: e.target.value}))}
                              className="h-8 text-sm"
                              placeholder="Notes de cuisson"
                            />
                          </div>
                        </div>
                        
                        <div className="flex gap-2 justify-center">
                          <Button
                            onClick={() => saveEditCooking(record.id)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Save className="h-3 w-3 mr-1" />
                            Sauvegarder
                          </Button>
                          <Button
                            onClick={cancelEditCooking}
                            size="sm"
                            variant="outline"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Annuler
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* Mode affichage normal */
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center">
                            <Flame className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{record.product_name}</h4>
                            <p className="text-sm text-gray-600">
                              🕒 Cuit le {formatDate(record.cooking_date)} à {formatTime(record.cooking_time)}
                            </p>
                            {record.notes && (
                              <p className="text-xs text-gray-500 mt-1">💭 {record.notes}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <Badge className="bg-orange-100 text-orange-800 font-semibold mb-2">
                            {record.quantity_cooked} cuits
                          </Badge>
                          <p className="text-xs text-green-600 mb-2">
                            ➡️ Envoyé au guichet
                          </p>
                          
                          {/* Boutons de gestion */}
                          {canManage() && (
                            <div className="flex gap-1 justify-end">
                              <button
                                onClick={() => startEditCooking(record)}
                                className="p-1 bg-orange-100 hover:bg-orange-200 rounded transition-colors"
                                title="Modifier cette cuisson"
                                data-testid={`edit-cooking-${record.id}`}
                              >
                                <Edit className="h-3 w-3 text-orange-600" />
                              </button>
                              <button
                                onClick={() => deleteCookingRecord(record.id, record.product_name)}
                                className="p-1 bg-red-100 hover:bg-red-200 rounded transition-colors"
                                title="Supprimer cette cuisson"
                                data-testid={`delete-cooking-${record.id}`}
                              >
                                <Trash2 className="h-3 w-3 text-red-600" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Timer className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Aucune cuisson aujourd'hui</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default ViennoiserieProduction;